import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * When the `prioritize-available` attribute is present, collection
 * products are displayed with available products first.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * Products that are unavailable.
   *
   * They are held until all available products have been
   * encountered in the collection.
   *
   * @type {HTMLElement[]}
   */
  #unavailableProducts = [];

  /**
   * Shopify pages already processed for availability.
   *
   * @type {Set<number>}
   */
  #availabilityProcessedPages = new Set();

  /**
   * The next Shopify page to inspect for availability.
   *
   * @type {number | null}
   */
  #nextAvailabilityPage = null;

  /**
   * Whether all Shopify pages have been inspected.
   *
   * @type {boolean}
   */
  #availabilityComplete = false;

  /**
   * Whether availability prioritization is enabled.
   *
   * @returns {boolean}
   */
  get #prioritizeAvailable() {
    return this.hasAttribute('prioritize-available');
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');

    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    /*
     * Set up availability prioritization before normal
     * Tinker pagination starts.
     */
    if (this.#prioritizeAvailable) {
      this.#initializeAvailabilityPriority();
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await viewTransition(
      () => this.#setLayout(value),
      ['product-grid']
    );

    requestIdleCallback(() => {
      const viewport = window.matchMedia('(min-width: 750px)').matches
        ? 'desktop'
        : 'mobile';

      sessionStorage.setItem(
        `product-grid-view-${viewport}`,
        value
      );
    });
  };

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;

    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector(
          '[data-grid-layout="desktop-default-option"]'
        )
      : this.querySelector(
          '[data-grid-layout="mobile-option"]'
        );

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Initialize availability prioritization.
   *
   * The initial Shopify page is immediately divided into
   * available and unavailable products.
   */
  #initializeAvailabilityPriority() {
    const { grid } = this.refs;

    if (!grid) return;

    const cards = Array.from(
      grid.querySelectorAll(':scope > [ref="cards[]"]')
    );

    if (!cards.length) return;

    const page = Number(cards[0]?.dataset.page);

    if (!Number.isFinite(page)) return;

    /*
     * Reset availability state.
     */
    this.#unavailableProducts = [];
    this.#availabilityProcessedPages.clear();
    this.#availabilityComplete = false;

    /*
     * The initial page has already been rendered by Liquid,
     * so process it immediately.
     */
    this.#availabilityProcessedPages.add(page);

    this.#nextAvailabilityPage = page + 1;

    const availableProducts = [];
    const unavailableProducts = [];

    for (const card of cards) {
      if (card.dataset.productAvailable === 'true') {
        availableProducts.push(card);
      } else {
        unavailableProducts.push(card);
      }
    }

    /*
     * Keep sold-out products out of the visible grid.
     */
    this.#unavailableProducts.push(
      ...unavailableProducts
    );

    /*
     * Replace the initial grid with available products only.
     */
    grid.replaceChildren(
      ...availableProducts
    );
  }

  /**
   * Determines whether a Shopify page can be used.
   *
   * @param {{ page: number, url?: URL } | undefined} pageInfo
   * @returns {boolean}
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage) return false;

    if (
      pageInfo.page < 1 ||
      pageInfo.page > Number(lastPage)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Fetch a page using Tinker's normal pagination logic.
   *
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    /*
     * Availability mode has its own next-page tracking.
     */
    if (
      this.#prioritizeAvailable &&
      type === 'next'
    ) {
      const page = this.#nextAvailabilityPage;

      if (!page) return;

      const url = new URL(window.location.href);

      url.searchParams.set(
        'page',
        page.toString()
      );

      url.hash = '';

      if (this.#shouldUsePage({ page, url })) {
        await this.#fetchSpecificPage(page, url);
      }

      return;
    }

    const page = this.#getPage(type);

    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(
      page.page,
      page.url
    );

    resolvePromise();
  }

  /**
   * Fetch a specific Shopify page.
   *
   * @param {number} pageNumber
   * @param {URL} [url]
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = {
      page: pageNumber,
      url,
    };

    if (!url) {
      const newUrl = new URL(window.location.href);

      newUrl.searchParams.set(
        'page',
        pageNumber.toString()
      );

      newUrl.hash = '';

      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) {
      return;
    }

    if (this.pages.has(pageNumber)) {
      return;
    }

    const pageContent =
      await sectionRenderer.getSectionHTML(
        this.sectionId,
        true,
        pageInfo.url
      );

    this.pages.set(
      pageNumber,
      pageContent
    );
  }

  /**
   * Render the next page.
   */
  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    if (this.#prioritizeAvailable) {
      await this.#renderNextPagePrioritized();
      return;
    }

    /*
     * ----------------------------------------------------------
     * ORIGINAL TINKER BEHAVIOR
     * ----------------------------------------------------------
     */
    const nextPage = this.#getPage('next');

    if (
      !nextPage ||
      !this.#shouldUsePage(nextPage)
    ) {
      return;
    }

    let nextPageItemElements =
      this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      this.#fetchPage('next');

      await promise;

      nextPageItemElements =
        this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) return;
    }

    grid.append(
      ...nextPageItemElements
    );

    this.#aspectRatioHelper?.processNewElements();

    history.pushState(
      '',
      '',
      nextPage.url.toString()
    );

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * Render the next Shopify page while prioritizing
   * available products.
   */
  async #renderNextPagePrioritized() {
    const { grid } = this.refs;

    if (!grid) return;

    /*
     * If every Shopify page has been processed, release
     * the held sold-out products.
     */
    if (this.#availabilityComplete) {
      if (this.#unavailableProducts.length) {
        grid.append(
          ...this.#unavailableProducts
        );

        this.#unavailableProducts = [];

        this.#aspectRatioHelper?.processNewElements();
      }

      return;
    }

    const pageNumber =
      this.#nextAvailabilityPage;

    if (!pageNumber) return;

    const pageUrl = new URL(
      window.location.href
    );

    pageUrl.searchParams.set(
      'page',
      pageNumber.toString()
    );

    pageUrl.hash = '';

    /*
     * Make sure the page exists.
     */
    if (
      !this.#shouldUsePage({
        page: pageNumber,
        url: pageUrl,
      })
    ) {
      this.#availabilityComplete = true;

      /*
       * No more available products exist.
       * Release the held sold-out products.
       */
      if (this.#unavailableProducts.length) {
        grid.append(
          ...this.#unavailableProducts
        );

        this.#unavailableProducts = [];

        this.#aspectRatioHelper?.processNewElements();
      }

      return;
    }

    /*
     * Use a prefetched page if available.
     */
    let pageItemElements =
      this.#getGridForPage(pageNumber);

    /*
     * Otherwise fetch it.
     */
    if (!pageItemElements) {
      await this.#fetchSpecificPage(
        pageNumber,
        pageUrl
      );

      pageItemElements =
        this.#getGridForPage(pageNumber);

      if (!pageItemElements) return;
    }

    const cards = Array.from(
      pageItemElements
    );

    /*
     * Mark this page as processed.
     */
    this.#availabilityProcessedPages.add(
      pageNumber
    );

    /*
     * Move to the following Shopify page.
     */
    this.#nextAvailabilityPage =
      pageNumber + 1;

    const availableProducts = [];
    const unavailableProducts = [];

    /*
     * Separate available products from sold-out products.
     */
    for (const card of cards) {
      if (
        card.dataset.productAvailable === 'true'
      ) {
        availableProducts.push(card);
      } else {
        unavailableProducts.push(card);
      }
    }

    /*
     * Add available products to the visible grid.
     */
    if (availableProducts.length) {
      grid.append(
        ...availableProducts
      );

      this.#aspectRatioHelper?.processNewElements();
    }

    /*
     * Keep sold-out products in their original
     * Shopify order.
     */
    if (unavailableProducts.length) {
      this.#unavailableProducts.push(
        ...unavailableProducts
      );
    }

    /*
     * Keep Tinker's URL behavior.
     */
    history.pushState(
      '',
      '',
      pageUrl.toString()
    );

    /*
     * Check whether this was the last Shopify page.
     */
    const lastPage =
      Number(grid.dataset.lastPage);

    if (
      Number.isFinite(lastPage) &&
      pageNumber >= lastPage
    ) {
      this.#availabilityComplete = true;

      /*
       * All available products have now been encountered.
       * Sold-out products can safely be added after them.
       */
      if (this.#unavailableProducts.length) {
        grid.append(
          ...this.#unavailableProducts
        );

        this.#unavailableProducts = [];

        this.#aspectRatioHelper?.processNewElements();
      }

      return;
    }

    /*
     * If this page contained no available products,
     * immediately continue to the next page.
     *
     * This prevents a completely sold-out Shopify page
     * from stopping the availability search.
     */
    if (!availableProducts.length) {
      await this.#renderNextPagePrioritized();
      return;
    }

    /*
     * Prefetch the next Shopify page.
     */
    requestIdleCallback(() => {
      if (!this.#nextAvailabilityPage) return;

      const nextUrl = new URL(
        window.location.href
      );

      nextUrl.searchParams.set(
        'page',
        this.#nextAvailabilityPage.toString()
      );

      nextUrl.hash = '';

      this.#fetchSpecificPage(
        this.#nextAvailabilityPage,
        nextUrl
      );
    });
  }

  /**
   * Observe the infinite-scroll elements.
   */
  #observeViewMore() {
    const {
      viewMorePrevious,
      viewMoreNext,
    } = this.refs;

    if (
      !viewMorePrevious &&
      !viewMoreNext
    ) {
      return;
    }

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver =
        new IntersectionObserver(
          async (entries) => {
            if (viewTransition.current) {
              await viewTransition.current;
            }

            for (const entry of entries) {
              if (!entry.isIntersecting) {
                continue;
              }

              const {
                viewMorePrevious,
                viewMoreNext,
              } = this.refs;

              if (
                entry.target ===
                viewMorePrevious
              ) {
                this.#renderPreviousPage();
              } else if (
                entry.target ===
                viewMoreNext
              ) {
                this.#renderNextPage();
              }
            }
          },
          {
            rootMargin: '100px',
          }
        );
    }

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(
        viewMorePrevious
      );
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(
        viewMoreNext
      );
    }
  }

  /**
   * Render the previous page.
   *
   * This preserves Tinker's original behavior.
   */
  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage =
      this.#getPage('previous');

    if (
      !previousPage ||
      !this.#shouldUsePage(previousPage)
    ) {
      return;
    }

    let previousPageItemElements =
      this.#getGridForPage(
        previousPage.page
      );

    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise =
          res;
      });

      this.#fetchPage('previous');

      await promise;

      previousPageItemElements =
        this.#getGridForPage(
          previousPage.page
        );

      if (!previousPageItemElements) {
        return;
      }
    }

    const scrollTop = window.scrollY;

    const firstElement =
      grid.firstElementChild;

    const oldHeight = firstElement
      ? firstElement.getBoundingClientRect().top +
        window.scrollY
      : 0;

    grid.prepend(
      ...previousPageItemElements
    );

    this.#aspectRatioHelper?.processNewElements();

    history.pushState(
      '',
      '',
      previousPage.url.toString()
    );

    if (firstElement) {
      const newHeight =
        firstElement.getBoundingClientRect().top +
        window.scrollY;

      const heightDiff =
        newHeight - oldHeight;

      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;

    const isPrevious =
      type === 'previous';

    if (!Array.isArray(cards)) {
      return;
    }

    const targetCard =
      cards[
        isPrevious
          ? 0
          : cards.length - 1
      ];

    if (!targetCard) {
      return;
    }

    const currentCardPage =
      Number(targetCard.dataset.page);

    if (!Number.isFinite(currentCardPage)) {
      return;
    }

    const page =
      isPrevious
        ? currentCardPage - 1
        : currentCardPage + 1;

    const url = new URL(
      window.location.href
    );

    url.searchParams.set(
      'page',
      page.toString()
    );

    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * Get the product grid from a cached Shopify section.
   *
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML =
      this.pages.get(page);

    if (!pageHTML) {
      return;
    }

    const parsedPage =
      new DOMParser().parseFromString(
        pageHTML,
        'text/html'
      );

    const gridElement =
      parsedPage.querySelector(
        '[ref="grid"]'
      );

    if (!gridElement) {
      return;
    }

    return gridElement.querySelectorAll(
      ':scope > [ref="cards[]"]'
    );
  }

  get sectionId() {
    const id =
      this.getAttribute('section-id');

    if (!id) {
      throw new Error(
        'The section-id attribute is required'
      );
    }

    return id;
  }

  /**
   * Handle filter updates.
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    this.#unavailableProducts = [];
    this.#availabilityProcessedPages.clear();
    this.#nextAvailabilityPage = null;
    this.#availabilityComplete = false;

    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const currentLastPage =
      this.refs.grid?.dataset.lastPage;

    const observer =
      new MutationObserver(() => {
        const newLastPage =
          this.refs.grid?.dataset.lastPage;

        /*
         * Tinker may update the existing grid without
         * changing the number of pages. Therefore we
         * also check whether cards have changed.
         */
        const hasCards =
          this.refs.grid?.querySelector(
            ':scope > [ref="cards[]"]'
          );

        if (
          newLastPage !== currentLastPage ||
          hasCards
        ) {
          observer.disconnect();

          if (!this.isConnected) {
            return;
          }

          if (this.#prioritizeAvailable) {
            this.#initializeAvailabilityPriority();
          }

          this.#observeViewMore();

          this.#fetchPage('next');
        }
      });

    const { grid } = this.refs;

    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: [
          'data-last-page',
        ],
        childList: true,
      });

      setTimeout(() => {
        observer.disconnect();
      }, 3000);
    }
  };
}